// The patient's door into a per-minute consult (contract §9 pins path and default export
// exactly). Navigates to OUR OWN room page (`/consultation/salle`, `ConsultRoom.jsx`) —
// never to the provider — so it is `ConsultRoomFrame` that ends up hosting the call and
// heartbeating for as long as the patient stays on our page. Disabled with an
// explanatory label until `canJoin`, so a patient can never be shown a live door to a
// call nobody has paid for yet.
//
// Gated on `canJoin`, NEVER on `authState === "authorised"` alone: `authState` answers
// only the money question and stays "authorised" long after a consult is over
// (gateway-api-contract.md §2), so a caller reading `GET /consults/{id}/status` derives
// `canJoin` itself and passes it straight through here. `reasonKey` is the already-
// resolved i18n key for the disabled label — the caller has the fuller status object
// (authState + sessionStatus + outcome) and is what picks it, so this button stays a
// dumb, reusable gate rather than re-deriving a partial copy of that logic.
//
// BUG #3 fix: this used to be a plain `<a href="/api/consults/{id}/join">`, a 302 that
// took the browser OFF our app entirely — nothing of ours kept running to heartbeat, and
// nothing on the practitioner side ever heartbeated either. A same-tab SPA navigation to
// our own room page is what lets `ConsultRoomFrame` (and its heartbeat) keep running for
// the whole visit, exactly the evidence the payment already earned.
import React from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { tenantHref } from "@/lib/tenantPath";
import { Video, Lock } from "lucide-react";

/**
 * @param {string} appointmentId
 * @param {string} token the patient's `manage_token` capability
 * @param {boolean} canJoin from `GET /consults/{id}/status` (contract §2) — the sole gate
 * @param {string} [reasonKey] i18n key for the disabled label; defaults to the generic
 *   "not available yet" reason when the caller has nothing more specific to say
 * @param {string} [reasonText] an ALREADY-RESOLVED sentence, for the one reason that is
 *   not a fixed string: the join window states an INSTANT ("your room opens at 14:45"),
 *   and interpolating a locale-formatted time is the caller's job, not this button's.
 *   Wins over `reasonKey` when both are supplied.
 */
export default function ConsultJoinButton({ appointmentId, token, canJoin, reasonKey,
                                            reasonText }) {
  const t = useT("public");
  const navigate = useNavigate();

  if (!canJoin) {
    return (
      <div data-testid="consult-join-disabled" className="inline-flex items-center gap-1.5 text-sm text-eh-muted">
        <Lock className="w-3.5 h-3.5" />
        <span>{reasonText || t(reasonKey || "consult.join.reasonNone")}</span>
      </div>
    );
  }

  const roomPath =
    `/consultation/salle?appointmentId=${encodeURIComponent(appointmentId)}&token=${encodeURIComponent(token)}`;

  return (
    <Button
      type="button"
      onClick={() => navigate(tenantHref(roomPath))}
      data-testid="consult-join-button"
    >
      <Video className="w-4 h-4 mr-1.5" /> {t("consult.join.action")}
    </Button>
  );
}
